/**
 * Mixpanel Tracking Configuration
 *
 * This file contains all tracking IDs and their corresponding configurations.
 * Add new tracking IDs here as you create new components.
 */

export interface TrackingConfig {
  intent: string;
  location?: string;
  category?: string;
  description?: string;
  /**
   * When true, suppresses tracking for elements using this ID.
   * Useful for globally disabling an ID without removing attributes.
   */
  disabled?: boolean;
}

export interface TrackingConfigMap {
  [trackingId: string]: TrackingConfig;
}

// Type-safe tracking ID type
export type TrackingId = keyof typeof TRACKING_CONFIG;

/**
 * Main tracking configuration
 * Add new tracking IDs here as you create components
 */
export const TRACKING_CONFIG: TrackingConfigMap = {
  // ===========================================
  // HEADER NAVIGATION
  // ===========================================
  "header-login": {
    intent: "Login Clicked",
    location: "Header",
    category: "Authentication",
    description: "Login button in header section",
  },
  "header-start-trial": {
    intent: "Start Trial Clicked",
    location: "Header",
    category: "Conversion",
    description: "Start Free Trial button in header section",
    disabled: true,
  },
  "header-start-free-trial": {
    intent: "Start Trial Clicked",
    location: "Header",
    category: "Conversion",
    description: "Start Free Trial button in header section (new ID)",
  },
  "header-contact-us": {
    intent: "Contact Us Clicked",
    location: "Header",
    category: "Conversion",
    description: "Contact Us button in header section",
  },
  "header-book-a-demo": {
    intent: "Book Demo Clicked",
    location: "Header",
    category: "Conversion",
    description: "Book a Demo button in header section",
  },

  // Header - Mobile
  "header-mobile-login": {
    intent: "Mobile Login Clicked",
    location: "Header - Mobile",
    category: "Authentication",
    description: "Login button in mobile header menu",
  },
  "header-mobile-book-a-demo": {
    intent: "Mobile Book Demo Clicked",
    location: "Header - Mobile",
    category: "Conversion",
    description: "Book a Demo button in mobile header menu",
  },
  "header-mobile-contact-us": {
    intent: "Mobile Contact Us Clicked",
    location: "Header - Mobile",
    category: "Conversion",
    description: "Contact Us button in mobile header menu",
  },

  // ===========================================
  // HERO SECTION
  // ===========================================
  "hero-start-free-trial": {
    intent: "Start Trial Clicked",
    location: "Hero Section",
    category: "Conversion",
    description: "Start Free Trial button in hero section",
  },
  "hero-book-a-demo": {
    intent: "Book Demo Clicked",
    location: "Hero Section",
    category: "Conversion",
    description: "Book Demo button in hero section",
  },
  "hero-contact-us": {
    intent: "Contact Us Clicked",
    location: "Hero Section",
    category: "Conversion",
    description: "Contact Us button in hero section",
  },

  // ===========================================
  // CTA SECTIONS
  // ===========================================
  "cta-book-a-demo": {
    intent: "Book Demo Clicked",
    location: "CTA Section",
    category: "Conversion",
    description: "Book Demo button in CTA section",
  },
  "cta-start-free-trial": {
    intent: "Start Trial Clicked",
    location: "CTA Section",
    category: "Conversion",
    description: "Start Free Trial button in CTA section",
  },
  "cta-drop-query": {
    intent: "Drop Query Clicked",
    location: "CTA Section",
    category: "Conversion",
    description: "Drop Query button in CTA section",
  },
  "cta-book-consultation": {
    intent: "Book Consultation Clicked",
    location: "CTA Section",
    category: "Conversion",
    description: "Book consultation button in CTA section",
  },
  "cta-schedule-demo": {
    intent: "Schedule Demo Clicked",
    location: "CTA Section",
    category: "Conversion",
    description: "Schedule demo button in CTA section",
  },
  "cta-request-info": {
    intent: "Request Info Clicked",
    location: "CTA Section",
    category: "Conversion",
    description: "Request info button in CTA section",
  },
  "cta-discover-insta-stream": {
    intent: "Discover Event Stream Clicked",
    location: "CTA Section",
    category: "Conversion",
    description: "Discover Event Stream button in CTA section",
  },
  "cta-contact-us": {
    intent: "Contact Us Clicked",
    location: "CTA Section",
    category: "Conversion",
    description: "Contact Us button in CTA section",
  },
  "cta-read-case-study": {
    intent: "Read Case Study Clicked",
    location: "CTA Section",
    category: "Conversion",
    description: "Read case study button in CTA section",
  },
  "cta-know-more": {
    intent: "Know More Clicked",
    location: "CTA Section",
    category: "Conversion",
    description: "Know more button in CTA section",
  },
  "cta-start-free-trial-how-to-steps": {
    intent: "Start Free Trial Clicked",
    location: "How To Steps Section",
    category: "Conversion",
    description: "Start free trial button in How To Steps section",
  },


  // ===========================================
  // OTHER SECTIONS
  // ===========================================
  "other-discover-integrations": {
    intent: "Discover Integrations Clicked",
    category: "Navigation",
    description: "Discover integrations button",
  },
  "other-get-more-details": {
    intent: "Get More Details Clicked",
    category: "Navigation",
    description: "Get more details button",
  },
  "other-schedule-callback": {
    intent: "Schedule Callback Clicked",
    category: "Conversion",
    description: "Schedule callback button",
  },
  "other-book-consultation": {
    intent: "Book Consultation Clicked",
    category: "Conversion",
  },
  "digital-menu-board-book-a-free-consultation": {
    intent: "Book Consultation Clicked",
    category: "Conversion",
  },
  "other-drop-query-security": {
    intent: "Drop Query Clicked",
    category: "Conversion",
  },
  "other-secure-network": {
    intent: "Secure Network Clicked",
    category: "Navigation",
  },
  "other-start-free-trial": {
    intent: "Start Trial Clicked",
    category: "Conversion",
  },
  "other-book-a-demo": {
    intent: "Book Demo Clicked",
    category: "Conversion",
  },
  // privilege-contact-us
  "privilege-contact-us": {
    intent: "Contact Us Clicked",
    category: "Conversion",
  },
  "homepage-see-all-solutions": {
    intent: "See All Solutions Clicked",
    category: "Navigation",
  },
  "homepage-see-all-compatible-devices": {
    intent: "See All Compatible Devices Clicked",
    category: "Navigation",
  },
  "homepage-read-case-studies": {
    intent: "Read Case Studies Clicked",
    category: "Navigation",
  },
  "homepage-see-all-integrations": {
    intent: "See All Integrations Clicked",
    category: "Navigation",
  },
  "homepage-contact-us": {
    intent: "Contact Us Clicked",
    category: "Conversion",
  },
  "contact-us-submitted": {
    intent: "Contact Us Submitted",
    category: "Conversion",
  },
  "book-demo-submitted": {
    intent: "Book Demo Submitted",
    category: "Conversion",
  },
  "mobile-onprem-book-a-demo": {
    intent: "Book Demo Clicked",
    category: "Conversion",
  },
  "mobile-onprem-contact-us": {
    intent: "Contact Us Clicked",
    category: "Conversion",
  },
  "mobile-single-book-demo-book-a-demo": {
    intent: "Book Demo Clicked",
    category: "Conversion",
  },
"mobile-start-free-trial": {
  intent: "Start Free Trial Clicked",
  category: "Conversion",
},
"mobile-contact-us": {
  intent: "Contact Us Clicked",
  category: "Conversion",
},
"hero-contact-us-utm": {
  intent: "Contact Us Clicked",
  category: "Conversion",
},
"hero-book-a-demo-utm": {
  intent: "Book Demo Clicked",
  category: "Conversion",
},
"final-cta-book-a-demo-utm": {
  intent: "Book Demo Clicked",
  category: "Conversion",
},
"final-cta-primary": {
  intent: "Primary Button Clicked",
  category: "Conversion",
},
"final-cta-secondary": {
  intent: "Secondary Button Clicked",
  category: "Conversion",
},
"cta-book-a-demo-utm": {
  intent: "Book Demo Clicked",
  category: "Conversion",
},
"mobile-book-a-demo-utm": {
  intent: "Book Demo Clicked",
  category: "Conversion",
},
"mobile-book-a-demo": {
  intent: "Book Demo Clicked",
  category: "Conversion",
},
"corporate-book-demo": {
  intent: "Book Demo Clicked",
  category: "Conversion",
},
"pricing-book-a-demo-utm": {
  intent: "Book Demo Clicked",
  category: "Conversion",
},


  // ===========================================
  // EVENT LANDING PAGES
  // ===========================================
  "registration-qr-hero": {
    intent: "Visitor Registration QR",
    location: "Event Hero",
    category: "Conversion",
    description: "Official InfoComm visitor registration QR in the hero",
  },
  "registration-qr-section": {
    intent: "Visitor Registration QR",
    location: "Event Registration Section",
    category: "Conversion",
    description: "Official InfoComm visitor registration QR in the closing navy section",
  },
  "pickcelgpt-demo": {
    intent: "PickcelGPT Section Viewed",
    location: "Event PickcelGPT Section",
    category: "Engagement",
    description: "PickcelGPT product reveal section on the event landing page",
  },
  "registration-hero-cta": {
    intent: "Visitor Registration Clicked",
    location: "Event Hero",
    category: "Conversion",
    description: "Primary registration button in the event hero",
  },
  "registration-qr-section-button": {
    intent: "Visitor Registration Clicked",
    location: "Event Registration Section",
    category: "Conversion",
    description: "Tap-to-register button shown instead of the QR on small screens",
  },
  "digital-workplace-partner-hero-cta": {
    intent: "Digital Workplace Partner CTA Clicked",
    location: "Digital Workplace Partner Hero",
    category: "Conversion",
    description: "Explore Partnership button in the digital workplace partner hero",
  },
  "digital-workplace-partner-hero-specialist": {
    intent: "Digital Workplace Partner Specialist Clicked",
    location: "Digital Workplace Partner Hero",
    category: "Conversion",
    description: "Request a Solution Briefing button in the digital workplace partner hero",
  },
  "digital-workplace-partner-cta-click": {
    intent: "Digital Workplace Partner CTA Clicked",
    location: "Digital Workplace Partner Page",
    category: "Conversion",
    description: "In-page partnership or capability CTA on the digital workplace partner page",
  },
  "digital-workplace-partner-form-start": {
    intent: "Digital Workplace Partner Form Started",
    location: "Digital Workplace Partner Form",
    category: "Conversion",
    description: "Partner lead form first interaction",
  },
  "digital-workplace-partner-form-submit": {
    intent: "Digital Workplace Partner Form Submitted",
    location: "Digital Workplace Partner Form",
    category: "Conversion",
    description: "Partner lead form submit on the digital workplace partner page",
  },
  "digital-workplace-partner-client-opportunity": {
    intent: "Digital Workplace Partner Client Opportunity Clicked",
    location: "Digital Workplace Partner Page",
    category: "Conversion",
    description: "Discuss a client use case or opportunity CTA",
  },
  "hardware-quote-form-start": {
    intent: "Hardware Quote Form Started",
    location: "Hardware Quote Form",
    category: "Conversion",
    description: "Hardware quote form first interaction",
  },
  "hardware-quote-form-submit": {
    intent: "Hardware Quote Form Submitted",
    location: "Hardware Quote Form",
    category: "Conversion",
    description: "Hardware quote form submit on the hardware get-a-quote page",
  },
};

/**
 * Get tracking configuration for a specific ID
 */
export function getTrackingConfig(trackingId: string): TrackingConfig | null {
  const config = TRACKING_CONFIG[trackingId] || null;
  if (!config) {
    console.warn(`Tracking ID "${trackingId}" not found in configuration. Please add it to lib/tracking/config.ts`);
  }
  return config;
}

/**
 * Get all tracking IDs for a specific location
 */
export function getTrackingIdsByLocation(location: string): string[] {
  return Object.entries(TRACKING_CONFIG)
    .filter(([_, config]) => config.location === location)
    .map(([id, _]) => id);
}

/**
 * Get all tracking IDs for a specific category
 */
export function getTrackingIdsByCategory(category: string): string[] {
  return Object.entries(TRACKING_CONFIG)
    .filter(([_, config]) => config.category === category)
    .map(([id, _]) => id);
}

/**
 * Validate that all tracking IDs in the config are properly formatted
 */
export function validateTrackingConfig(): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  Object.entries(TRACKING_CONFIG).forEach(([id, config]) => {
    // Check ID format (should be kebab-case)
    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(id)) {
      errors.push(`Invalid tracking ID format: "${id}" - should be kebab-case`);
    }

    // Check required fields
    if (!config.intent) {
      errors.push(`Missing intent for tracking ID: "${id}"`);
    }
    if (!config.location) {
      errors.push(`Missing location for tracking ID: "${id}"`);
    }
    if (!config.category) {
      errors.push(`Missing category for tracking ID: "${id}"`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
}
